import pino from 'pino';
import { config } from 'config';

const isDev = import.meta.env.DEV;
const BATCH_SIZE = 10;
const FLUSH_MS = 5000;

const batchBuffer: Record<string, unknown>[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

type TransportErrorHandler = (
  error: unknown,
  info: {
    endpoint: string;
    eventsCount: number;
  },
) => void;

let transportErrorHandler: TransportErrorHandler | null = null;

/** Зарегистрировать внешний обработчик ошибок транспорта (например, для Sentry). */
export function setLoggerTransportErrorHandler(handler: TransportErrorHandler | null): void {
  transportErrorHandler = handler;
}

/**
 * Убирает чувствительные поля (stack, componentStack) из лог-событий перед отправкой на наш API.
 * В prod не отправляем стеки на бэкенд, чтобы не утекали в логи/хранилище.
 */
function sanitizeLogEvent(event: Record<string, unknown>): Record<string, unknown> {
  const out = { ...event };
  if (out.err != null && typeof out.err === 'object' && !Array.isArray(out.err)) {
    const err = { ...(out.err as Record<string, unknown>) };
    delete err.stack;
    out.err = err;
  }
  delete out.stack;
  delete out.componentStack;
  if (out.extra != null && typeof out.extra === 'object' && !Array.isArray(out.extra)) {
    const extra = { ...(out.extra as Record<string, unknown>) };
    delete extra.componentStack;
    out.extra = extra;
  }
  return out;
}

function sendBatch(events: Record<string, unknown>[]): void {
  if (events.length === 0) return;
  const payload = isDev ? events : events.map((e) => sanitizeLogEvent(e as Record<string, unknown>));
  fetch(config.apiLogsUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ logs: payload }),
    keepalive: true,
  }).catch((error) => {
    // Не вызывать logger здесь: мы уже внутри пути отправки логов, вызов logger
    // снова приведёт к sendBatch → fetch → при ошибке снова сюда → рекурсия.
    // Передаём ошибку наружу через transportErrorHandler (например, в Sentry).
    if (transportErrorHandler) {
      try {
        transportErrorHandler(error, {
          endpoint: config.apiLogsUrl,
          eventsCount: events.length,
        });
      } catch (handlerError) {
        console.error('[logger] Ошибка обработчика ошибки транспорта', handlerError);
      }
    }

    // Всегда дублируем в консоль, чтобы не потерять информацию при отсутствии обработчика.
    console.error('[logger] Ошибка отправки логов', error);
  });
}

/** Снять таймер и отправить весь буфер. */
function doFlush(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (batchBuffer.length > 0) {
    sendBatch(batchBuffer.splice(0, batchBuffer.length));
  }
}

/** Отложенная отправка буфера по таймеру (чтобы редкие логи тоже уходили). */
function scheduleDelayedFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(doFlush, FLUSH_MS);
}

function transmitSend(
  _level: number | string,
  logEvent: Record<string, unknown>,
): void {
  batchBuffer.push(logEvent);
  if (batchBuffer.length >= BATCH_SIZE) {
    sendBatch(batchBuffer.splice(0, BATCH_SIZE));
  }
  scheduleDelayedFlush();
}

// Типы Pino для browser.transmit.send не совпадают с нашими (Level, LogEvent);
// приводим тип, чтобы не тянуть сюда типы из pino.
type PinoTransmitSend = (level: unknown, logEvent: unknown) => void;

const pinoLogger = pino({
  level: isDev ? 'debug' : 'info',
  browser: {
    asObject: false,
    write: undefined,
    ...(isDev
      ? {}
      : {
          transmit: {
            level: 'info',
            send: transmitSend as PinoTransmitSend,
          },
        }),
  },
});

/**
 * Отправить накопленные логи на сервер (например перед закрытием вкладки).
 * В dev ничего не делает.
 */
export function flushLogs(): Promise<void> {
  if (isDev) return Promise.resolve();
  doFlush();
  return Promise.resolve();
}

/** Обёртка с прежним API (message, context?, error?), чтобы не менять вызовы. */
export const logger = {
  debug(message: string, context?: Record<string, unknown>): void {
    pinoLogger.debug(context ?? {}, message);
  },
  info(message: string, context?: Record<string, unknown>): void {
    pinoLogger.info(context ?? {}, message);
  },
  warn(message: string, context?: Record<string, unknown>, error?: Error): void {
    pinoLogger.warn({ ...context, err: error }, message);
  },
  error(message: string, context?: Record<string, unknown>, error?: Error): void {
    pinoLogger.error({ ...context, err: error }, message);
  },
  flush: flushLogs,
};
